export const calendarNews = {
    id: 'calendar-news',
    title: 'Economic calendar and news',
    summary: 'Read the economic calendar, understand inflation, rate decisions, jobs reports and company results, trade carefully around news, and use the AI brief as information.',
    level: 'intermediate',
    art: 'gold',
    image: { src: '/academy/calendar-news.webp', alt: 'A gold coin in front of a dark calendar page with a glowing date' },
    screen: { to: '/markets', label: 'Markets' },
    lessons: [
        {
            id: 'reading-the-economic-calendar',
            title: 'Reading the economic calendar',
            minutes: 5,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'An **economic calendar** lists scheduled data releases and events that can move markets, such as inflation figures, jobs reports and interest rate decisions. When the data is available, it is shown on the Markets page.' },
                {
                    type: 'list',
                    items: [
                        '**Time**: shown in your time zone. Events already out are greyed.',
                        '**Country and event**: who publishes it and what it is.',
                        '**Impact**: one to three bars for low, medium and high. Switch between **Medium+** and **High** to filter.',
                        '**Actual, Est. and Prev.**: the new figure once it is out, the average forecast, and the last figure.',
                        '**Markets**: the instruments most exposed to that country, such as its currency pairs and indices. Click one to open it.',
                    ],
                },
                { type: 'p', text: 'The **Next big event** box in the market pulse at the top of the page counts down to the next high-impact release. An instrument’s quick view lists the **Upcoming events** that affect it.' },
                { type: 'example', title: 'Reading one row', text: 'A US inflation release shows three bars, Est. 3.1% and Prev. 3.2%. Thirty minutes later it shows Actual 3.4%. The figure came in well above the forecast, which is the kind of surprise that moves markets.' },
                { type: 'tip', text: 'Press the bell next to an event to be reminded 5, 15, 30 or 60 minutes before it. Your reminders are listed in Account → Alerts with your other alerts.' },
            ],
            quiz: [
                {
                    q: 'What does “Est.” mean on a calendar row?',
                    options: ['The figure that was released', 'The average forecast before the release', 'The figure from last time'],
                    answer: 1,
                    why: 'Est. is the forecast. Actual is the new figure and Prev. is the last one.',
                },
                {
                    q: 'In which time zone are calendar times shown?',
                    options: ['New York time', 'UTC', 'Your own time zone'],
                    answer: 2,
                    why: 'The calendar converts every event to your time zone.',
                },
                {
                    q: 'How can you be reminded before an event?',
                    options: ['Press the bell next to it', 'Write to support', 'You cannot'],
                    answer: 0,
                    why: 'The bell sets an economic event alert, which you can manage in Account → Alerts.',
                },
            ],
            tryIt: { to: '/markets', label: 'Open the economic calendar' },
            terms: ['economic-calendar', 'impact-level'],
        },
        {
            id: 'cpi-and-rate-decisions',
            title: 'Inflation (CPI) and rate decisions',
            minutes: 5,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'The **consumer price index (CPI)** measures how fast the prices people pay are rising. It is usually published monthly, as a change on a year earlier. **Core CPI** leaves out food and energy, which jump around, and is watched closely too.' },
                { type: 'p', text: 'Central banks, such as the US Federal Reserve, the European Central Bank and the Bank of England, set interest rates to keep inflation near a target. They meet on dates published well in advance. A **rate decision** is the outcome of one of those meetings, often followed by a press conference.' },
                {
                    type: 'list',
                    items: [
                        'Inflation above forecast can make markets expect higher rates for longer.',
                        'Higher expected rates have often supported a currency and weighed on shares and gold.',
                        'Lower expected rates have often done the opposite.',
                        'These are tendencies, not rules. The reaction depends on what was already expected.',
                    ],
                },
                { type: 'example', title: 'Expected vs surprise', text: 'Everyone expects a central bank to raise rates by 0.25%, and it does. The currency barely moves, because the rise was already in the price. At the next meeting it holds rates when a rise was expected, and the currency drops 0.8% in minutes.' },
                { type: 'p', text: 'What the bank says about the future can matter more than the decision itself. Markets often move again during the press conference.' },
                { type: 'warn', text: 'Rate decisions and CPI releases are high-impact events. Prices can move fast in both directions before settling.' },
            ],
            quiz: [
                {
                    q: 'What does CPI measure?',
                    options: ['How fast consumer prices are rising', 'The number of new jobs', 'A central bank’s interest rate'],
                    answer: 0,
                    why: 'CPI tracks the change in the prices people pay for goods and services.',
                },
                {
                    q: 'A central bank raises rates exactly as everyone expected. What often happens?',
                    options: ['A very large move', 'Little reaction, because it was already priced in', 'Markets close for the day'],
                    answer: 1,
                    why: 'Markets react to surprises. An expected decision is usually already reflected in prices.',
                },
            ],
            tryIt: { to: '/trade/EURUSD', label: 'Open EUR/USD' },
            terms: ['cpi', 'rate-decision', 'economic-calendar'],
        },
        {
            id: 'jobs-reports-big-releases',
            title: 'Jobs reports and other big releases',
            minutes: 4,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'The US **non-farm payrolls (NFP)** report counts how many jobs the US economy added in the past month, leaving out farm workers. It usually comes out on the first Friday of the month at 08:30 New York time, together with the unemployment rate and average hourly earnings.' },
                { type: 'p', text: 'Markets watch it because a strong job market can keep wages and inflation up, which feeds into rate decisions. The dollar, US indices and gold often move sharply in the minutes after it.' },
                {
                    type: 'list',
                    items: [
                        '**GDP**: how fast the whole economy grew.',
                        '**Retail sales**: how much consumers are spending.',
                        '**PMI surveys**: whether purchasing managers see business growing or shrinking.',
                        '**Central bank minutes and speeches**: hints about future rate decisions.',
                    ],
                },
                { type: 'example', title: 'A revision changes the story', text: 'Payrolls are expected at 180,000 and come in at 220,000, a beat. But last month’s figure is revised down from 200,000 to 140,000. Taken together, the two months are weaker than they first looked, and the first jump in the dollar fades.' },
                { type: 'tip', text: 'Look at the Prev. column too. A big revision to an earlier figure can matter as much as the new one.' },
            ],
            quiz: [
                {
                    q: 'What does the NFP report count?',
                    options: ['Jobs added in the US, excluding farm workers', 'Company profits', 'Inflation'],
                    answer: 0,
                    why: 'Non-farm payrolls is the monthly change in US jobs outside farming.',
                },
                {
                    q: 'A new figure beats the forecast, but last month’s is revised down a lot. What is true?',
                    options: ['Only the new figure matters', 'The revision can change how the report is read', 'Revisions are never published'],
                    answer: 1,
                    why: 'Markets look at the whole picture, including changes to earlier figures.',
                },
            ],
            tryIt: { to: '/trade/GOLD', label: 'Open gold' },
            terms: ['nfp', 'economic-calendar'],
        },
        {
            id: 'company-news-earnings-surprises',
            title: 'Company news and earnings surprises',
            minutes: 4,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'Shares move on company news: results, product launches, deals, legal cases and changes at the top. When the data is available, a stock’s quick view on the Markets page shows its recent **News** headlines and a **Company** section with the next earnings date and the EPS estimate.' },
                { type: 'p', text: 'An **earnings surprise** is the gap between the reported result and the estimate. A big surprise either way can move a share several percent at the next open.' },
                { type: 'example', title: 'Working out a surprise', text: 'The EPS estimate is $2.00 and the company reports $2.30. The surprise is $0.30, or 15% above the estimate. If it had reported $1.80, that would be a 10% miss.' },
                {
                    type: 'list',
                    items: [
                        'Read the headline, then check the time it was published. Old news is usually already in the price.',
                        'A headline is one source. Look for the company’s own statement before you rely on it.',
                        'The quick view also shows analyst ratings and price targets from a third party. They are opinions, not facts, and not ours.',
                    ],
                },
                { type: 'warn', text: 'Reacting to a headline in seconds is risky. The first move after news often reverses, and spreads can be wide.' },
            ],
            quiz: [
                {
                    q: 'The EPS estimate is $1.00 and the company reports $1.20. What is the surprise?',
                    options: ['20% above the estimate', '20% below the estimate', 'No surprise'],
                    answer: 0,
                    why: '$0.20 above a $1.00 estimate is a 20% beat.',
                },
                {
                    q: 'What are the analyst ratings in a stock’s quick view?',
                    options: ['Our recommendation', 'Third-party opinions', 'Guaranteed price targets'],
                    answer: 1,
                    why: 'They come from outside analysts and are information only, not advice.',
                },
            ],
            tryIt: { to: '/trade/NVDA', label: 'Open NVIDIA' },
            terms: ['earnings', 'eps', 'earnings-surprise'],
        },
        {
            id: 'news-and-events',
            title: 'Trading around news: spreads, gaps and slippage',
            minutes: 5,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'Around a big release, three things change at once: prices move faster, spreads can widen, and orders may fill away from the price you expected.' },
                {
                    type: 'list',
                    items: [
                        '**Wider spreads**: when the underlying market gets busy or thin, its spread widens, and so can the spread you trade at. Every trade pays the spread, so it costs more to open and close.',
                        '**Gaps**: news that comes out while a market is closed can make it open at a very different price.',
                        '**Slippage**: in a fast move, a stop loss or pending order fills at the next available price, which can be worse than the level you set.',
                    ],
                },
                { type: 'example', title: 'A rate decision', text: 'You hold EUR/USD with a stop 0.3% away. A central bank surprises the market and the pair moves 1% in a minute. Your stop triggers, but the price jumped past it, so the trade closes at the next available price, well beyond the stop.' },
                { type: 'p', text: 'Check the economic calendar on the Markets page before you trade and before you leave trades open, and set a reminder with the bell for the events that matter to you. The trade screen shows whether a market is open, and marks a price as **Indicative price** when its live feed is unavailable.' },
                { type: 'tip', text: 'Many traders trade smaller around big releases, or stay out until the first move has settled.' },
                { type: 'warn', text: 'Trading the release itself is closer to a gamble than a plan. A stop loss limits most losses, but not a jump straight past it.' },
            ],
            quiz: [
                {
                    q: 'A big release is due in five minutes and your stop is close. What can happen?',
                    options: ['The stop always fills exactly at its price', 'The price can jump past the stop and fill worse', 'Stops are paused during news'],
                    answer: 1,
                    why: 'Fast moves can carry the price past a stop, which then closes at the next available price.',
                },
                {
                    q: 'What is slippage?',
                    options: ['A fill at a different price from the one you set', 'A daily swap charge', 'A type of order'],
                    answer: 0,
                    why: 'Slippage is the gap between the price you set or saw and the price you actually got.',
                },
                {
                    q: 'Where can you check upcoming releases on the platform?',
                    options: ['The economic calendar on the Markets page', 'Account → Security', 'There is no calendar'],
                    answer: 0,
                    why: 'The Markets page shows the economic calendar when the data is available.',
                },
            ],
            tryIt: { to: '/markets', label: 'Check the calendar' },
            terms: ['slippage', 'gap', 'spread', 'stop-loss', 'economic-calendar'],
        },
        {
            id: 'ai-brief-why-moving',
            title: 'The AI brief and “why is this moving?”',
            minutes: 4,
            level: 'intermediate',
            blocks: [
                { type: 'p', text: 'When it is switched on, the Markets page has two short summaries written by AI, both marked **AI**.' },
                {
                    type: 'list',
                    items: [
                        '**Morning brief**: one per day for everyone. It covers what moved most, the themes behind it, and what is on the calendar.',
                        '**Why is it moving?**: in an instrument’s quick view, press **Explain the recent move** for a short explanation of that market. The same answer is shared for about 30 minutes, and each client can ask a limited number of times per hour.',
                    ],
                },
                { type: 'p', text: 'Both are written from our prices, the economic calendar and recent headlines. The AI is told to use only that data, never to tell you to buy, sell or hold, never to give price targets, and never to predict where a price will go. If the data shows no clear reason for a move, it should say so.' },
                { type: 'p', text: 'Treat them as information to check, not as a recommendation. AI can misread a headline, link a move to the wrong cause, or miss news that is not in its data.' },
                { type: 'steps', items: ['Read the summary.', 'Check the calendar row or headline it mentions.', 'Look at the chart: does the timing of the move match?', 'Decide for yourself, using your own plan and risk limits.'] },
                { type: 'warn', text: 'An explanation of why a price moved says nothing reliable about where it goes next.' },
            ],
            quiz: [
                {
                    q: 'What is the AI brief?',
                    options: ['A recommendation to trade', 'A summary of prices, events and headlines, for information', 'A forecast of tomorrow’s prices'],
                    answer: 1,
                    why: 'It summarises data and is told never to give advice, targets or forecasts.',
                },
                {
                    q: '“Why is it moving?” names a headline as the cause. What should you do?',
                    options: ['Trade on it straight away', 'Check the headline and the chart yourself', 'Assume it is always right'],
                    answer: 1,
                    why: 'AI can be wrong. Check what it says against the source and the timing of the move.',
                },
            ],
            tryIt: { to: '/markets', label: 'Read the morning brief' },
            terms: ['ai-brief', 'economic-calendar'],
        },
    ],
};
export const calendarNewsTerms = [
    { id: 'economic-calendar', term: 'Economic calendar', text: 'A list of scheduled data releases and events, such as rate decisions and jobs figures, with their impact, forecast and previous figure. Shown on the Markets page when the data is available.', lesson: 'reading-the-economic-calendar' },
    { id: 'impact-level', term: 'Impact level', text: 'How much an economic event usually moves markets: low, medium or high, shown as one to three bars in the calendar.', lesson: 'reading-the-economic-calendar' },
    { id: 'cpi', term: 'CPI (consumer price index)', text: 'A measure of how fast the prices people pay are rising, usually published monthly. Core CPI leaves out food and energy.', lesson: 'cpi-and-rate-decisions' },
    { id: 'rate-decision', term: 'Rate decision', text: 'The outcome of a central bank meeting that sets interest rates. Markets react to how it compares with what was expected and to what the bank says next.', lesson: 'cpi-and-rate-decisions' },
    { id: 'nfp', term: 'Non-farm payrolls (NFP)', text: 'The monthly count of jobs added in the US outside farming, usually released on the first Friday of the month at 08:30 New York time.', lesson: 'jobs-reports-big-releases' },
    { id: 'earnings-surprise', term: 'Earnings surprise', text: 'The gap between a company’s reported earnings and the analyst estimate, often given as a percentage of the estimate.', lesson: 'company-news-earnings-surprises' },
    { id: 'slippage', term: 'Slippage', text: 'The difference between the price you set or saw and the price your order actually filled at. It is most likely in fast markets and around news.', lesson: 'news-and-events' },
    { id: 'ai-brief', term: 'AI brief', text: 'A short daily market summary on the Markets page, written by AI from our prices, the calendar and headlines. Information only, never advice or a forecast.', lesson: 'ai-brief-why-moving' },
];
//# sourceMappingURL=calendar-news.js.map